import MobileNav from "@/components/mobileNav";
import AppSidebar from "@/components/sidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { useGetRecentTransactions, useGetUser } from "@/hooks/use-queries";
import { AuthProvider } from "@/lib/AuthProvider";
import { useCardsStore, useUser } from "@/zustand/store";
import * as Sentry from "@sentry/react";
import { useEffect, useRef } from "react";
import { Outlet } from "react-router-dom";
import { CardCreationNoticeModal } from "@/components/card-unavailable";
import SideSheet from "@/components/sheets";
import Modal from "@/components/modals/index";
import { AnimatePresence } from "framer-motion";
import { motion } from "framer-motion";
import { ErrorBoundary } from "react-error-boundary";
import ErrorFallback from "@/components/error-fallback";
import Logo from "@/components/logo";
import { Loader2 } from "lucide-react";
import { useModalStore } from "@/zustand/modalStore";
import { FASTER_FUNDING_STORAGE_KEY } from "@/components/modals/contents/faster-funding";
import {
  recordSapphireMaintenanceView,
  shouldShowSapphireMaintenance,
} from "@/components/modals/contents/sapphire-maintenance";

const DashboardLayout = () => {
  const { data: user, isLoading: loading } = useGetUser();
  const { isLoading: transactionsIsLoading } = useGetRecentTransactions();
  const { isCardUnavailableOpen } = useCardsStore((state) => state);
  const { openModal } = useModalStore();
  const entryModalShown = useRef(false);

  useEffect(() => {
    useUser.setState({ user: user });
    if (user) {
      Sentry.setUser({
        id: user.id,
        username: `${user.firstName} ${user.lastName}`,
        email: user.email,
      });
    }
  }, [user]);

  // One entry notice per visit. Only one modal can be open at a time, so the
  // maintenance notice takes priority and faster funding waits for a later visit.
  useEffect(() => {
    if (loading || transactionsIsLoading || entryModalShown.current) return;
    entryModalShown.current = true;

    if (shouldShowSapphireMaintenance()) {
      recordSapphireMaintenanceView();
      openModal("sapphireMaintenance");
    } else if (!localStorage.getItem(FASTER_FUNDING_STORAGE_KEY)) {
      openModal("fasterFunding");
    }
  }, [loading, transactionsIsLoading, openModal]);

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      {/* `theme-user` scopes the dark theme to the user side. Admin and auth
          render outside it and stay on the light theme. See docs/RETHEME.md. */}
      <div className="theme-user">
        <AuthProvider>
          <AnimatePresence>
            {loading || transactionsIsLoading ? (
              <motion.div
                key="loader"
                initial={{ opacity: 1 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-dark-background-main fixed inset-0 z-[9999] flex items-center justify-center"
              >
                <div className="flex flex-col items-center gap-4">
                  <Logo className="text-dark-text-50 h-8 w-auto" />
                  <Loader2 className="text-dark-primary-main size-7 animate-spin" />
                </div>
              </motion.div>
            ) : (
              <SidebarProvider>
                <AppSidebar />
                <MobileNav />
                <main className="bg-dark-background-main border-dark-stroke-5 relative min-h-dvh w-full border-b-[0.5px] pb-20 lg:pb-6">
                  {<Outlet />}
                </main>
                {/* <ChatSupport /> */}
              </SidebarProvider>
            )}
          </AnimatePresence>
          <SideSheet />
          <Modal />
          {isCardUnavailableOpen && (
            <CardCreationNoticeModal
              onClose={() => {
                localStorage.removeItem("isCardUnavailableOpen");
                useCardsStore.setState({ isCardUnavailableOpen: null });
              }}
            />
          )}
        </AuthProvider>
      </div>
    </ErrorBoundary>
  );
};

export default DashboardLayout;
